import { JSX, useMemo, useRef, useState } from 'react';
import { useCallback } from 'react';
import { ICourseRequirement, ICreateCourseRequirement } from '@/types/courses';
import { BrandliveEvent } from '@/types/projects.types';
import { Session, SessionTypesEnum } from '@/types/session.types';
import Popover from '@ui/popover/popover';
import { ICONS } from '@ui/icons/types';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import { getRequirementMapBySession } from '../../utils/course-utils';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import Container from '@ui/container/container';
import { TGlobalContainerDesigns } from '@styles/design-system/global-components/global-components.types';

type Props = {
  selectedSession: number;
  workingEvent: BrandliveEvent | undefined | null;
  onChange: (session: number) => void;
  requirements?: (ICourseRequirement | ICreateCourseRequirement)[];
  selectedType?: ICreateCourseRequirement['type'];
};

export default function SessionSelector(props: Props): JSX.Element {
  const portalEl = useRef<HTMLDivElement | null>(null);

  const [recalculatePosition, setRecalculatePosition] = useState(0);

  const title = useMemo(() => {
    return (
      props.workingEvent?.sessions.find((session) => session.session === props.selectedSession)?.title.base || 'Select'
    );
  }, [props.selectedSession, props.workingEvent]);

  const disabled = useCallback(
    (session: Session) => {
      // currently only broadcast sessions can have requirements
      // so if this session type is not broadcast it cannot be added
      if (session.session_type !== SessionTypesEnum.broadcast) {
        return true;
      }

      // sanity check
      if (!props.selectedType) {
        return true;
      }

      // create requirements map to prevent nested loops
      const requirementsMap = getRequirementMapBySession(props.requirements);

      const disabled = requirementsMap[session.session]?.includes(props.selectedType);

      // if session has the selected requirement then it cannot be added
      return disabled;
    },
    [props.requirements, props.selectedType]
  );

  return (
    <Popover
      closeOnClick
      backgroundColor="var(--gray-700)"
      icon={ICONS.KEYBOARD_ARROW_DOWN}
      portalEl={portalEl}
      width="fit-content"
      cycleWithArrowKeys
      onOpen={() => setRecalculatePosition(Math.random())}
      renderTriggerButton={({ open, closing }) => {
        return (
          <Button width="fit-content" className="padding-t-10 padding-0 flex ai-center jc-center gap-10">
            <Text className="H4" color="var(--gray-400)">
              {title}
            </Text>
            <RotatableIcon
              name={ICONS.KEYBOARD_ARROW_DOWN}
              color="var(--gray-400)"
              size={12}
              rotate={open && !closing ? '180deg' : '0'}
            />
          </Button>
        );
      }}
      recalcutePosition={recalculatePosition}
    >
      {({ triggerBounds }) => (
        <Container
          className="position-fixed flex-column overflow-auto"
          borderRadius="8px"
          design={TGlobalContainerDesigns.GlobalCont1}
          style={{
            top: triggerBounds?.top || 0,
            left: (triggerBounds?.left || 0) + (triggerBounds?.width || 0),
            transform: `translate(-${triggerBounds?.width ?? 1 / 2}px, 40px)`,
            width: 'fit-content',
            maxWidth: '600px',
            zIndex: 1200,
            maxHeight: '200px',
          }}
        >
          {props.workingEvent?.sessions?.map((session, i) => (
            <Button
              key={session.uuid}
              width="fit-content"
              design="LocalBttnSec"
              borderRadius="0"
              onClick={() => props.onChange(session.session)}
              data-testid={`session-selector-${i}`}
              disabled={disabled(session)}
            >
              <Text clamp={3} whiteSpace="normal">
                {session.title.base}
              </Text>
            </Button>
          ))}
        </Container>
      )}
    </Popover>
  );
}
